import React, { FC, useCallback } from 'react'
import styles from 'styles/components/card.module.scss'
import text from 'styles/utility/text.module.scss'
import { Card, StockCard } from 'components/Card'
import { HorizontalLine, Spacer } from 'components/Utility'
import { OrangeButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { notifyStocks } from 'reducks/stock/operations'
import { NotificationModal } from 'components/Modal'
import { openModal } from 'reducks/modal/operations'
import { BreadType } from 'types/bread'
import { State } from 'index'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { Role } from 'lib/role'
import { getLastOrderTime, getOrderStartTime } from 'reducks/store/selectors'
import { dateToString, parseTimeToDate } from 'lib/helper'
import { NotificationDescription } from 'components/Typography'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { getOrders } from 'reducks/checkout/selectors'
import { OrderType } from 'types/checkout'
import { BusinessTimeType } from 'types/store'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { useEarliestBusinessTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { useIsOrderable } from 'hooks/store'
import { isSameDay } from 'date-fns'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { useCanBeNotified, useLastNotifiedMinutes } from 'hooks/stock'
import { findNotifiedStock } from 'lib/stock'
import { StockType } from 'types/stock'
const daysOfWeek = new DaysOfWeek()

type Props = {
  breads: BreadType[]
  isEditable?: boolean
  page: 'released' | 'unreleased'
  stocks: StockType[]
}

const StockCardList: FC<Props> = (props) => {
  const { breads, isEditable, page, stocks } = props
  const dispatch = useDispatch()

  const closedDates = useSelector((state: State) => getClosedDates(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const role = useSelector((state: State) => getRole(state))
  const roleManager = new Role(role, storeRef)
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))

  const now = new Date()
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(now, specialHolidays)
  const temporaryOpen = useGetTemporaryOpen(dateToString(now), storeCalendars)
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpen)
  const earliestLastOrderTime = useEarliestBusinessTime([dayOfWeek], lastOrderTime, 'last_order_time', temporaryOpen)
  const orderStartDateTime = parseTimeToDate(latestOrderStartTime)
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isOrderable = useIsOrderable(now, orderStartDateTime, lastOrderDateTime)
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, now))

  const canBeNotified = useCanBeNotified(stocks)
  const lastNotifiedMinutes = useLastNotifiedMinutes(stocks)

  const checkoutItems = useCheckoutItemsFromOrders(orders)

  const notifyStocksCallback = useCallback(async () => {
    const notifiedStocks: StockType[] = []
    for (let i = 0; i < breads.length; i = (i + 1) | 0) {
      const bread = breads[i]
      const stocksOfBread = stocks.filter((stock) => bread.bread_ref && stock.bread_ref.id === bread.bread_ref.id)
      const stock = findNotifiedStock(now, stocksOfBread, checkoutItems)
      if (!stock) continue
      notifiedStocks.push(stock)
    }
    dispatch(notifyStocks(notifiedStocks))
    dispatch(openModal(<NotificationModal />, '', ''))
  }, [stocks, checkoutItems, dispatch, now])

  return (
    <Card>
      {breads.length > 0 ? (
        <>
          <div>
            {breads.map((bread) => {
              if (!bread.bread_ref) return
              const breadStocks = stocks.filter((stock) => stock.bread_ref.id === bread.bread_ref?.id)
              // 前営業日より以前に削除された在庫データは「販売中」タブに表示しない
              if (page === 'released' && breadStocks.every((stock) => stock.is_deleted)) return
              const checkoutItemsOfBread = checkoutItems.filter((checkoutItem) => {
                return checkoutItem.bread_ref.id === bread.bread_ref?.id
              })
              return (
                <StockCard
                  key={bread.bread_ref.id}
                  bread={bread}
                  checkoutItems={checkoutItemsOfBread}
                  isEditable={isEditable}
                  stocks={breadStocks}
                />
              )
            })}
          </div>
          {!isEditable && (
            <div>
              <HorizontalLine verticalMargin={16} />
              {!canBeNotified && (
                <>
                  <NotificationDescription align={'center'} lastNotifiedMinutes={lastNotifiedMinutes} />
                  <Spacer size={8} />
                </>
              )}
              <div className={styles.row__inner}>
                <OrangeButton
                  disabled={!canBeNotified || !isOrderable || isClosed || !roleManager.hasEnoughRole(5)}
                  iconPath={'/icon/white/bell.svg'}
                  label={'お客さまに通知する'}
                  onClick={notifyStocksCallback}
                  size={'medium'}
                  type={'main'}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <p className={`${text.font_bold} ${text.font_large} ${text.align_center}`} data-cy={'stock-zero'}>
          {page === 'released' ? '販売中' : '未販売'}の商品はありません
        </p>
      )}
    </Card>
  )
}

export default StockCardList
